<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import InboxesAPI from 'dashboard/api/inboxes';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CopyableSpan from 'dashboard/components/CopyableSpan.vue';
import BuzzerBridgeRegistration from './components/BuzzerBridgeRegistration.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const isToggling = ref(false);

// Zadarma is a voice-only channel, so only WhatsApp inboxes get the switch.
const hasSwitch = computed(
  () => props.inbox.channel_type === INBOX_TYPES.WHATSAPP
);

const toggleVoiceCalls = async enabled => {
  isToggling.value = true;
  try {
    await InboxesAPI.setVoiceCalls(props.inbox.id, enabled);
    await store.dispatch('inboxes/get', props.inbox.id);
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE')
    );
  } finally {
    isToggling.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <SettingsToggleSection
      v-if="hasSwitch"
      :model-value="inbox.voice_enabled"
      :header="t('INBOX_MGMT.VOICE_CALLS.ENABLE.LABEL')"
      :description="t('INBOX_MGMT.VOICE_CALLS.ENABLE.DESCRIPTION')"
      :hide-toggle="isToggling"
      @update:model-value="toggleVoiceCalls"
    >
      <template v-if="isToggling" #hiddenToggle>
        <Spinner class="size-4 text-n-slate-11" />
      </template>
    </SettingsToggleSection>

    <template v-if="inbox.voice_enabled">
      <SettingsFieldSection
        :label="t('INBOX_MGMT.VOICE_CALLS.PHONE_NUMBER.LABEL')"
        :help-text="t('INBOX_MGMT.VOICE_CALLS.PHONE_NUMBER.HELP_TEXT')"
      >
        <CopyableSpan :text="inbox.phone_number" />
      </SettingsFieldSection>
      <BuzzerBridgeRegistration :inbox="inbox" />
    </template>
  </div>
</template>
